import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClub } from '../App';
import { toast } from 'sonner';
import api from '../utils/api';

import { Search, Plus, Users, Trash2, Filter, ChevronDown } from 'lucide-react';
import { RatingBadge } from '../components/RatingBadge';
import AddPlayerModal from '../components/AddPlayerModal';
import FindPlayersToInvite from '../components/FindPlayersToInvite';

export default function PlayersPage() {
  const { user } = useAuth();
  const { selectedClub } = useClub();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ country: '', city: '', availability: '' });
  const [gameType, setGameType] = useState('doubles');

  const canManage = selectedClub?.my_role === 'admin' || selectedClub?.my_role === 'contributor';
  const isAdmin = selectedClub?.my_role === 'admin';

  const activeFilterCount = [filters.country, filters.city, filters.availability].filter(Boolean).length;

  const fetchPlayers = () => {
    if (!selectedClub) return;
    const params = new URLSearchParams({ group_id: selectedClub.id });
    if (filters.country) params.append('country', filters.country);
    if (filters.city) params.append('city', filters.city);
    if (filters.availability) params.append('availability', filters.availability);
    params.append('include_club_stats', 'true');
    api.get(`/players?${params.toString()}`).then(r => setPlayers(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { fetchPlayers(); }, [selectedClub, filters]);

  const clearFilters = () => setFilters({ country: '', city: '', availability: '' });

  const filtered = players.filter(p => (p.name || '').toLowerCase().includes(search.toLowerCase()));

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete player "${p.name}"? This will remove them from all tournaments and delete their account.`)) return;
    setDeleting(p.id);
    try {
      await api.delete(`/players/${p.id}`);
      toast.success(`Player "${p.name}" deleted`);
      fetchPlayers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete player');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Players</h1>
          <p className="text-[11px] text-slate-500">
            {activeFilterCount > 0 ? `${players.length} matching` : `${players.length} players`} · {selectedClub?.name}
          </p>
        </div>
        {canManage && (
          <button onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-all active:scale-95"
            data-testid="add-player-btn">
            <Plus size={13} /> Manage
          </button>
        )}
      </div>

      {isAdmin && selectedClub && (
        <FindPlayersToInvite club={selectedClub} />
      )}

      <div className="space-y-2">
        <div className="flex gap-1.5">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
            <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full h-8 pl-8 pr-3 rounded-lg border border-slate-200 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="search-players-input" />
          </div>
          <select
            value={gameType}
            onChange={e => setGameType(e.target.value)}
            className="h-8 px-2 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            data-testid="game-type-select"
          >
            <option value="doubles">Dbl</option>
            <option value="singles">Sgl</option>
          </select>
          <button
            onClick={() => setShowFilters(f => !f)}
            className={`h-8 px-2 rounded-lg border text-xs font-medium flex items-center gap-1 transition-all ${
              showFilters || activeFilterCount > 0
                ? 'bg-green-50 border-green-300 text-green-700'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
            }`}
            data-testid="toggle-filters-btn"
          >
            <Filter size={12} />
            {activeFilterCount > 0 && (
              <span className="w-4 h-4 rounded-full bg-green-600 text-white text-[9px] flex items-center justify-center">{activeFilterCount}</span>
            )}
            <ChevronDown size={10} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-wrap gap-2 items-end" data-testid="filter-bar">
            <div className="flex-1 min-w-[100px]">
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5">Country</label>
              <input type="text" placeholder="e.g. USA" value={filters.country}
                onChange={e => setFilters(f => ({ ...f, country: e.target.value }))}
                className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="filter-country" />
            </div>
            <div className="flex-1 min-w-[100px]">
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5">City</label>
              <input type="text" placeholder="e.g. London" value={filters.city}
                onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
                className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="filter-city" />
            </div>
            <div className="min-w-[110px]">
              <label className="block text-[10px] font-medium text-slate-500 mb-0.5">Availability</label>
              <select value={filters.availability}
                onChange={e => setFilters(f => ({ ...f, availability: e.target.value }))}
                className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="filter-availability">
                <option value="">All</option>
                <option value="available">Available</option>
                <option value="busy">Busy</option>
                <option value="away">Away</option>
              </select>
            </div>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters}
                className="h-8 px-2 rounded-lg text-[10px] font-medium text-red-600 hover:bg-red-50 border border-red-200 transition-all"
                data-testid="clear-filters-btn">
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="players-table">
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-2 py-1.5 text-left font-semibold text-slate-500 uppercase">Player</th>
                <th className="px-1 py-1.5 text-right font-semibold text-slate-500 uppercase">Club</th>
                <th className="px-1 py-1.5 text-right font-semibold text-slate-500 uppercase">Life</th>
                <th className="px-1 py-1.5 text-right font-semibold text-slate-500 uppercase">GP</th>
                <th className="px-1 py-1.5 text-right font-semibold text-slate-500 uppercase">W/L</th>
                <th className="px-1 py-1.5 text-right font-semibold text-slate-500 uppercase">W%</th>
                <th className="px-1 py-1.5 text-right font-semibold text-slate-500 uppercase">Miss</th>
                <th className="hidden sm:table-cell px-1 py-1.5 text-right font-semibold text-slate-500 uppercase">PF</th>
                <th className="hidden sm:table-cell px-1 py-1.5 text-right font-semibold text-slate-500 uppercase">PA</th>
                <th className="px-1 py-1.5 text-right font-semibold text-slate-500 uppercase">±R</th>
                {isAdmin && <th className="px-1 py-1.5 w-7"></th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {Array.from({ length: isAdmin ? 11 : 10 }).map((__, j) => (
                      <td key={j} className="px-2 py-2"><div className="h-3 bg-slate-100 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={isAdmin ? 11 : 10} className="px-4 py-10 text-center">
                  <Users className="mx-auto w-7 h-7 text-slate-300 mb-1.5" />
                  <p className="text-xs text-slate-500">{search ? 'No players match' : 'No players yet'}</p>
                </td></tr>
              ) : (
                filtered.map((p, i) => {
                  const clubRating = gameType === 'singles' ? (p.singles_rating || 1500) : (p.doubles_rating || 1500);
                  const lifetimeRating = gameType === 'singles' ? (p.lifetime_singles_rating || 1500) : (p.lifetime_doubles_rating || 1500);
                  const gamesPlayed = gameType === 'singles' ? (p.singles_games || 0) : (p.doubles_games || 0);
                  const wins = gameType === 'singles' ? (p.singles_wins || 0) : (p.doubles_wins || 0);
                  const losses = gameType === 'singles' ? (p.singles_losses || 0) : (p.doubles_losses || 0);
                  const winRate = gamesPlayed > 0 ? Math.round(wins / gamesPlayed * 100) : 0;
                  const ratingDelta = gameType === 'singles' ? (p.singles_delta ?? 0) : (p.doubles_delta ?? 0);
                  const picUrl = p.picture_url ? `${process.env.REACT_APP_BACKEND_URL}/api/players/${p.id}/picture` : null;
                  return (
                    <tr key={p.id} className={`hover:bg-slate-50 transition-colors ${i < filtered.length - 1 ? 'border-b border-slate-100' : ''}`} data-testid={`player-row-${p.id}`}>
                      <td className="px-2 py-1.5">
                        <Link to={`/players/${p.id}`} className="flex items-center gap-1.5 group">
                          {picUrl ? (
                            <img src={picUrl} alt={p.name} className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold text-[9px] flex-shrink-0">
                              {p.name?.[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div className="min-w-0">
                            <span className="font-medium text-slate-900 group-hover:text-green-700 transition-colors block truncate max-w-[80px] sm:max-w-[120px]">{p.name}</span>
                            {(p.city || p.country) && (
                              <span className="text-[9px] text-slate-400 block truncate max-w-[80px]">{[p.city, p.country].filter(Boolean).join(', ')}</span>
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-1 py-1.5 text-right">
                        <span className="font-bold text-slate-900 font-mono">{clubRating}</span>
                        {p.rating_status && <div className="mt-0.5 flex justify-end"><RatingBadge status={p.rating_status} /></div>}
                      </td>
                      <td className="px-1 py-1.5 text-right font-mono text-slate-500">{lifetimeRating}</td>
                      <td className="px-1 py-1.5 text-right font-mono text-slate-700">{gamesPlayed}</td>
                      <td className="px-1 py-1.5 text-right">
                        <span className="text-green-600 font-medium">{wins}</span><span className="text-slate-300 mx-0.5">/</span><span className="text-red-500 font-medium">{losses}</span>
                      </td>
                      <td className="px-1 py-1.5 text-right">
                        <span className={`font-semibold ${winRate >= 50 ? 'text-green-600' : 'text-slate-500'}`}>{winRate}%</span>
                      </td>
                      <td className="px-1 py-1.5 text-right font-mono text-red-400">{p.missed_games ?? 0}</td>
                      <td className="hidden sm:table-cell px-1 py-1.5 text-right font-mono text-slate-700">{p.club_pf ?? '-'}</td>
                      <td className="hidden sm:table-cell px-1 py-1.5 text-right font-mono text-slate-700">{p.club_pa ?? '-'}</td>
                      <td className="px-1 py-1.5 text-right font-medium">
                        <span className={ratingDelta >= 0 ? 'text-green-600' : 'text-red-500'}>{ratingDelta >= 0 ? '+' : ''}{ratingDelta}</span>
                      </td>
                      {isAdmin && (
                        <td className="px-1 py-1.5 text-right">
                          <button
                            onClick={() => handleDelete(p)}
                            disabled={deleting === p.id}
                            className="p-0.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50"
                            title={`Delete ${p.name}`}
                            data-testid={`delete-player-btn-${p.id}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <AddPlayerModal
          groupId={selectedClub?.id}
          existingPlayerIds={players.map(p => p.user_id).filter(Boolean)}
          onClose={() => setShowModal(false)}
          onAdded={fetchPlayers}
        />
      )}
    </div>
  );
}
